/*
  When each answer on a card is open. The server checks all of this again; the browser only says in plain words
  why a button is off. Validate needs the card to have been displayed since its last change (DEC-1016-97, point 9)
  and an empty blocking set (DEC-1016-26). Note is never refused, so it has no gate here.
*/

import type { ElementRow, LineRow, SmeCardView } from "../../../api/types";
import { elementWaits, lineReturned, lineWaitsAlone } from "../shared/waits";
import { lineTitle } from "./lineText";

export interface Gate {
  open: boolean;
  /** Why it is closed, in plain words. */
  reason: string | null;
}

export function validateGate(card: SmeCardView): Gate {
  if (!elementWaits(card.element)) return { open: false, reason: "Nothing on the element itself is waiting for you." };
  if (card.blockers.length > 0) {
    const reasons = card.blockers.map((blocker) => blocker.message.trim().replace(/[.\s]+$/, "")).join("; ");
    return { open: false, reason: `Cannot validate yet: ${reasons}.` };
  }
  if (!card.displayed) return { open: false, reason: "Show the card first. Validate needs you to have looked at it since it last changed." };
  return { open: true, reason: null };
}

/** A line the SME can send back: it waits (alone, or inside a waiting element) and it is the team's own work. */
export function lineCanBeSentBack(line: LineRow, element: ElementRow): boolean {
  return !lineReturned(line) && line.origin === "work" && (lineWaitsAlone(line, element) || (elementWaits(element) && line.state !== "validated"));
}

export type AnswerKind = "send_back" | "reject" | "note";

/**
 * The places an answer can be about: the whole card, or one line or value. Send back and Reject on the whole card are
 * open only while the element itself waits; a note may always be about the whole card, and about any line.
 */
export function answerChoices(card: SmeCardView, kind: AnswerKind): Array<{ value: string; label: string }> {
  const element = card.element;
  const all = [...card.library_lines, ...card.lines];
  const lines =
    kind === "note" ? all : kind === "reject" ? all.filter((line) => lineWaitsAlone(line, element)) : all.filter((line) => lineCanBeSentBack(line, element));
  const whole = kind === "note" || elementWaits(element) ? [{ value: "", label: "The whole card" }] : [];
  return [...whole, ...lines.map((line) => ({ value: line.id, label: lineTitle(line) }))];
}
